/**
 * Member Retention & Engagement Intelligence Dashboard
 *
 * Provides visibility into:
 * - Member engagement health scores
 * - Risk level distribution
 * - At-risk member identification
 * - Contact attempt analytics
 * - Retention alerts management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Minus,
  Phone,
  Mail,
  MessageSquare,
  RefreshCw,
  ClipboardList,
  Bell,
  User,
  Gauge,
  Clock,
  AlertOctagon,
  Brain,
  HelpCircle,
  Loader2,
  X,
  FileText,
  MapPin,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/lib/authFetch';
import ChurnPredictionBadge from './ChurnPredictionBadge';
import MLPredictionInsightsPanel from './MLPredictionInsightsPanel';
import { RetentionGuidancePanel } from './RetentionGuidancePanel';
import { MemberActionPlanEnhanced } from './MemberActionPlanEnhanced';
import { MemberDataValidation } from './MemberDataValidation';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pagination } from '../common/Pagination';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { getWeaveinApiUrl } from '@/src/config';
import GeographicRiskMap from './GeographicRiskMap';
import CommunicationFeaturesPanel from './CommunicationFeaturesPanel';
import LivePredictionPanel from './LivePredictionPanel';
import ModelPerformanceDashboard from './ModelPerformanceDashboard';
import { CaseNotesAnalysis } from '../cbo/CaseNotesAnalysis';
import { CBODataEntry } from '../cbo/CBODataEntry';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
// Types
interface RetentionDashboardData {
  cbo_id: number;
  cbo_name: string;
  healthy_count: number;
  watch_count: number;
  at_risk_count: number;
  critical_count: number;
  lost_count: number;
  recovered_count: number;
  total_members: number;
  avg_engagement_score: number;
  avg_communication_score: number;
  avg_activity_score: number;
  avg_recency_score: number;
  avg_data_quality_score: number;
  improving_count: number;
  stable_count: number;
  declining_count: number;
  rapid_decline_count: number;
  open_alerts: number;
  critical_alerts: number;
  contact_attempts_30d: number;
  successful_contacts_30d: number;
  contact_success_rate_30d: number;
  // ML Prediction summary
  ml_predictions_available?: boolean;
  predicted_churn_14d?: number;
  ml_high_risk_count?: number;
  ml_critical_risk_count?: number;
  ml_model_version?: string;
  ml_model_accuracy?: number;
}

interface AtRiskMember {
  engagement_score_id: number;
  member_id: number;
  cbo_id: number;
  enrollment_id?: number;
  first_name: string;
  last_name: string;
  phone_number?: string;
  email?: string;
  engagement_score: number;
  risk_level: string;
  trend_direction: string;
  days_since_last_contact?: number;
  days_since_last_activity?: number;
  communication_score: number;
  activity_score: number;
  recency_score: number;
  data_quality_score: number;
  enrollment_status?: string;
  enrollment_progress?: number;
  program_name?: string;
  contact_attempts_7d: number;
  last_contact_outcome?: string;
  open_alert_count: number;
  preferred_channel?: string;
  best_day_of_week?: number;
  best_hour_start?: number;
  best_hour_end?: number;
  // ML Prediction fields
  churn_probability_14d?: number;
  ml_risk_level?: 'low' | 'moderate' | 'high' | 'critical';
}

interface RetentionAlert {
  id: number;
  member_id: number;
  cbo_id: number;
  enrollment_id?: number;
  alert_type: string;
  alert_severity: string;
  alert_title: string;
  alert_description?: string;
  status: string;
  assigned_to?: number;
  assigned_at?: string;
  sla_due_at?: string;
  sla_breached: boolean;
  created_at: string;
  member_name?: string;
  engagement_score?: number;
  risk_level?: string;
}

// Risk level color mapping
const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel) {
    case 'healthy':
      return 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-200 dark:hover:bg-green-900/70';
    case 'watch':
      return 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-900/70';
    case 'at_risk':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 dark:hover:bg-yellow-900/70';
    case 'critical':
    case 'lost':
      return 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-900/70';
    case 'recovered':
      return 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-200 dark:hover:bg-green-900/70';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600';
  }
};

const getRiskBadgeVariant = (riskLevel: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (riskLevel) {
    case 'healthy':
    case 'recovered':
      return 'default';
    case 'watch':
      return 'secondary';
    case 'at_risk':
      return 'outline';
    case 'critical':
    case 'lost':
      return 'destructive';
    default:
      return 'secondary';
  }
};

// Trend icon mapping
const getTrendIcon = (trend: string) => {
  switch (trend) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-yellow-600" />;
    case 'rapid_decline':
      return <TrendingDown className="h-4 w-4 text-red-600" />;
    default:
      return <Minus className="h-4 w-4 text-gray-400" />;
  }
};

// Channel icon mapping
const getChannelIcon = (channel?: string) => {
  switch (channel) {
    case 'phone_call':
      return <Phone className="h-4 w-4 text-green-600" />;
    case 'sms':
      return <MessageSquare className="h-4 w-4 text-blue-600" />;
    case 'email':
      return <Mail className="h-4 w-4 text-purple-600" />;
    default:
      return <User className="h-4 w-4 text-gray-500" />;
  }
};

// Day name mapping
const getDayName = (day?: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return day !== undefined ? days[day] : 'Unknown';
};

interface MemberRetentionDashboardProps {
  cboId: number;
}

export const MemberRetentionDashboard: React.FC<MemberRetentionDashboardProps> = ({ cboId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { token } = useAuth();
  const [tabValue, setTabValue] = useState('all-members');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [dashboardData, setDashboardData] = useState<RetentionDashboardData | null>(null);
  const [atRiskMembers, setAtRiskMembers] = useState<AtRiskMember[]>([]);
  const [allMembers, setAllMembers] = useState<AtRiskMember[]>([]);
  const [alerts, setAlerts] = useState<RetentionAlert[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter state for risk level
  const [selectedRiskFilter, setSelectedRiskFilter] = useState<string | null>(null);
  
  // Selected member for detailed views
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  // Pagination state for all members
  const [membersPage, setMembersPage] = useState(1);
  const [membersTotalPages, setMembersTotalPages] = useState(1);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);

  // Dialog state
  const [selectedMember, setSelectedMember] = useState<AtRiskMember | null>(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<RetentionAlert | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  // Guidance panel state
  const [showGuidance, setShowGuidance] = useState(false);

  // Communication dialog states
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [communicationMember, setCommunicationMember] = useState<any>(null);

  // Fetch all members with pagination
  const fetchAllMembers = useCallback(async (page: number = 1, riskFilter: string | null = null) => {
    if (!cboId || !token) return;

    try {
      setMembersLoading(true);

      let url = getWeaveinApiUrl(`/api/retention/all-members/${cboId}?page=${page}&page_size=25`);
      if (riskFilter) {
        url += `&risk_level=${riskFilter}`;
      }

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setAllMembers(data.members);
        setMembersTotal(data.total);
        setMembersTotalPages(data.total_pages);
        setMembersPage(data.page);
      }
    } catch (err) {
      console.error('Error fetching all members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, [cboId, token]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!cboId || !token) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard, at-risk members, and alerts in parallel
      const [dashboardRes, atRiskRes, alertsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl(`/api/retention/dashboard/${cboId}`)),
        authFetch(getWeaveinApiUrl(`/api/retention/at-risk-members/${cboId}?limit=50`)),
        authFetch(getWeaveinApiUrl(`/api/retention/alerts?cbo_id=${cboId}&status=open&limit=50`)),
      ]);

      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboardData(data);
      }

      if (atRiskRes.ok) {
        const data = await atRiskRes.json();
        setAtRiskMembers(data);
      }

      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data);
      }

      // Fetch all members separately with pagination
      await fetchAllMembers(1, null);
    } catch (err) {
      console.error('Error fetching retention data:', err);
      setError('Failed to load retention dashboard data');
    } finally {
      setLoading(false);
    }
  }, [cboId, token, fetchAllMembers]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Refetch members when filter or page changes
  useEffect(() => {
    if (!loading) {
      fetchAllMembers(membersPage, selectedRiskFilter);
    }
  }, [selectedRiskFilter, membersPage, fetchAllMembers, loading]);

  // Refresh scores
  const handleRefreshScores = async () => {
    if (!cboId || !token) return;

    try {
      setRefreshing(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/retention/scores/calculate?cbo_id=${cboId}`), {
        method: 'POST',
        jsonBody: { member_ids: null }
      });

      if (response.ok) {
        await fetchDashboardData();
      }
    } catch (err) {
      console.error('Error refreshing scores:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Acknowledge alert
  const handleAcknowledgeAlert = async (alertId: number) => {
    if (!token) return;

    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/retention/alerts/${alertId}/acknowledge`), {
        method: 'POST'
      });

      if (response.ok) {
        setAlerts((prev) =>
          prev.map((a) => (a.id === alertId ? { ...a, status: 'acknowledged' } : a))
        );
      }
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('retention.no_retention_data_available_engagement', 'No retention data available. Engagement scores need to be calculated first.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Toolbar (page title rendered by the shell chrome) */}
      <div className="flex flex-wrap items-center justify-end gap-2 mb-4 sm:mb-6">
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={showGuidance ? 'default' : 'outline'}
            onClick={() => setShowGuidance(!showGuidance)}
            size="sm"
          >
            <HelpCircle className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">{showGuidance ? 'Hide' : 'Guide'}</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleRefreshScores}
            disabled={refreshing}
            size="sm"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-1 sm:mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1 sm:mr-2" />
            )}
            <span className="text-xs sm:text-sm">{refreshing ? 'Calculating...' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      {/* Retention Guidance Panel */}
      {showGuidance && (
        <RetentionGuidancePanel
          variant="full"
          context="dashboard"
          onClose={() => setShowGuidance(false)}
        />
      )}

      {/* Inline Tip (always visible) */}
      <RetentionGuidancePanel variant="inline" context="dashboard" />

      {/* Summary Cards - Compact Design */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
        {/* Engagement Score */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {t('retention.avg_engagement_score', 'Avg Engagement')}
              </p>
              <Gauge className="h-4 w-4 text-primary" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardData.avg_engagement_score?.toFixed(1) || '-'}
            </div>
            <Progress
              value={dashboardData.avg_engagement_score || 0}
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>

        {/* AI Churn Prediction Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {t('retention.ai_churn_prediction', 'AI Churn (14d)')}
              </p>
              <Brain className="h-4 w-4 text-primary" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardData.predicted_churn_14d ?? Math.round((dashboardData.at_risk_count + dashboardData.critical_count) * 0.6)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Model: {dashboardData.ml_model_version || 'churn_v1.2'}
            </p>
          </CardContent>
        </Card>

        {/* At-Risk Count */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {t('retention.atrisk_members', 'At-Risk')}
              </p>
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardData.at_risk_count + dashboardData.critical_count}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {dashboardData.critical_count} critical
            </p>
          </CardContent>
        </Card>

        {/* Open Alerts */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {t('retention.open_alerts', 'Alerts')}
              </p>
              <div className="relative">
                <Bell className={`h-4 w-4 ${dashboardData.critical_alerts > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                {dashboardData.critical_alerts > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-600 rounded-full text-[8px] text-white flex items-center justify-center">
                    {dashboardData.critical_alerts}
                  </span>
                )}
              </div>
            </div>
            <div className="text-2xl font-bold">
              {dashboardData.open_alerts}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {dashboardData.critical_alerts} critical
            </p>
          </CardContent>
        </Card>

        {/* Contact Success Rate */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                Contact (30d)
              </p>
              <Phone className="h-4 w-4 text-primary" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardData.contact_success_rate_30d?.toFixed(0) || 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {dashboardData.successful_contacts_30d}/{dashboardData.contact_attempts_30d}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Distribution - Clickable Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-medium">
              {t('retention.risk_level_distribution', 'Risk Level Distribution')}
            </h3>
            {selectedRiskFilter && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSelectedRiskFilter(null); setMembersPage(1); }}
              >
                Clear Filter
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'healthy' ? null : 'healthy'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'healthy'
                  ? 'bg-green-600 text-white font-semibold'
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-200 dark:hover:bg-green-900/70'
              }`}
            >
              {t('retention.healthy', 'Healthy')} ({dashboardData.healthy_count})
            </button>
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'watch' ? null : 'watch'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'watch'
                  ? 'bg-blue-600 text-white font-semibold'
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-900/70'
              }`}
            >
              {t('retention.watch', 'Watch')} ({dashboardData.watch_count})
            </button>
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'at_risk' ? null : 'at_risk'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'at_risk'
                  ? 'bg-yellow-600 text-white font-semibold'
                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 dark:hover:bg-yellow-900/70'
              }`}
            >
              {t('retention.at_risk', 'At Risk')} ({dashboardData.at_risk_count})
            </button>
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'critical' ? null : 'critical'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'critical'
                  ? 'bg-red-600 text-white font-semibold'
                  : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-900/70'
              }`}
            >
              {t('retention.critical', 'Critical')} ({dashboardData.critical_count})
            </button>
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'lost' ? null : 'lost'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'lost'
                  ? 'bg-gray-600 text-white font-semibold'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('retention.lost', 'Lost')} ({dashboardData.lost_count})
            </button>
            <button
              onClick={() => { setSelectedRiskFilter(selectedRiskFilter === 'recovered' ? null : 'recovered'); setMembersPage(1); setTabValue('all-members'); }}
              className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-all ${
                selectedRiskFilter === 'recovered'
                  ? 'bg-green-600 text-white font-semibold'
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-200 dark:hover:bg-green-900/70 border-2 border-dashed border-green-300 dark:border-green-700'
              }`}
            >
              {t('retention.recovered', 'Recovered')} ({dashboardData.recovered_count})
            </button>
          </div>

          {/* Trend Summary - Inline */}
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4">
            <div className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-xs">{dashboardData.improving_count} improving</span>
            </div>
            <div className="flex items-center gap-1">
              <Minus className="h-4 w-4 text-gray-400" />
              <span className="text-xs">{dashboardData.stable_count} stable</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="h-4 w-4 text-yellow-600" />
              <span className="text-xs">{dashboardData.declining_count} declining</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="h-4 w-4 text-red-600" />
              <span className="text-xs">{dashboardData.rapid_decline_count} rapid decline</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Details */}
      <Card className="overflow-hidden">
        <Tabs value={tabValue} onValueChange={setTabValue}>
          {/* Horizontally scrollable tabs on mobile */}
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <TabsList className="w-max min-w-full justify-start border-b rounded-none h-auto p-0 flex-nowrap">
              <TabsTrigger value="all-members" className="relative whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">{t('retention.all_members', 'All Members')}</span>
                <span className="sm:hidden">All</span>
                <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">
                  {membersTotal || dashboardData.total_members}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="at-risk" className="relative whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">{t('retention.atrisk_members_1', 'At-Risk Members')}</span>
                <span className="sm:hidden">At-Risk</span>
                <Badge variant="destructive" className="ml-1 sm:ml-2 text-xs">
                  {atRiskMembers.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="alerts" className="relative whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                {t('retention.alerts', 'Alerts')}
                <Badge variant="destructive" className="ml-1 sm:ml-2 text-xs">
                  {alerts.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="geographic-risk-map" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Geographic Risk Map</span>
                <span className="sm:hidden">Map</span>
              </TabsTrigger>
              <TabsTrigger value="live-prediction" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Live Prediction</span>
                <span className="sm:hidden">Live</span>
              </TabsTrigger>
              <TabsTrigger value="model-performance" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Model Performance</span>
                <span className="sm:hidden">Model</span>
              </TabsTrigger>
              <TabsTrigger value="communication-features" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Communication Features</span>
                <span className="sm:hidden">Comms</span>
              </TabsTrigger>
              <TabsTrigger value="case-notes-analysis" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Case Notes Analysis</span>
                <span className="sm:hidden">Notes</span>
              </TabsTrigger>
              <TabsTrigger value="cbo-data-entry" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">CBO Data Entry</span>
                <span className="sm:hidden">Data</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* All Members Tab - Filtered by Risk Level */}
          <TabsContent value="all-members" className="py-6">
            {/* Header with filter info and pagination summary */}
            <div className="flex justify-between items-center mb-4">
              <div>
                {selectedRiskFilter ? (
                  <Alert className="py-2">
                    <AlertDescription>
                      Showing <strong>{selectedRiskFilter}</strong> members ({membersTotal} total)
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedRiskFilter(null); setMembersPage(1); }} className="ml-2">
                        Clear filter
                      </Button>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Showing {membersTotal} members (Page {membersPage} of {membersTotalPages})
                  </p>
                )}
              </div>
              {membersLoading && <Loader2 className="h-5 w-5 animate-spin" />}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-center">Risk</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Brain className="h-4 w-4 text-primary" />
                        Churn Risk
                      </div>
                    </TableHead>
                    <TableHead className="text-center">Trend</TableHead>
                    <TableHead className="text-center">Last Contact</TableHead>
                    <TableHead className="text-center">Best Channel</TableHead>
                    <TableHead className="text-center">Alerts</TableHead>
                    <TableHead className="text-center">ML Insights</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allMembers.map((member) => (
                    <TableRow key={member.engagement_score_id || member.member_id}>
                      <TableCell>
                        <div>
                          <div className="text-sm font-medium">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {member.program_name || 'No program'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm font-bold">
                            {member.engagement_score?.toFixed(0) || '-'}
                          </span>
                          {member.engagement_score && (
                            <Progress
                              value={member.engagement_score}
                              className="w-12 h-2"
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getRiskBadgeVariant(member.risk_level || 'unknown')}>
                          {member.risk_level || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <ChurnPredictionBadge
                          prediction={{
                            churn_probability_14d: member.churn_probability_14d,
                            ml_risk_level: member.ml_risk_level,
                          }}
                          horizon={14}
                          size="small"
                          showDetails={true}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              {getTrendIcon(member.trend_direction || 'stable')}
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.trend_direction || 'stable'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          {member.days_since_last_contact !== null && member.days_since_last_contact !== undefined
                            ? `${member.days_since_last_contact}d ago`
                            : 'Never'}
                        </div>
                        {member.last_contact_outcome && (
                          <div className="text-xs text-muted-foreground">
                            {member.last_contact_outcome}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              {getChannelIcon(member.preferred_channel)}
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.preferred_channel
                                ? `Best time: ${getDayName(member.best_day_of_week)} ${member.best_hour_start || 9}:00-${member.best_hour_end || 17}:00`
                                : 'No preference data'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-center">
                        {member.open_alert_count > 0 && (
                          <div className="relative inline-block">
                            <Bell className="h-4 w-4 text-red-600" />
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-600 rounded-full text-[8px] text-white flex items-center justify-center">
                              {member.open_alert_count}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      {/* ML Insights Column - Clickable icons to view member-specific ML data */}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                  onClick={() => {
                                    setSelectedMemberId(member.member_id);
                                    setTabValue('communication-features');
                                  }}
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Communication Features (ML)
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  onClick={() => {
                                    setSelectedMemberId(member.member_id);
                                    setTabValue('case-notes-analysis');
                                  }}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Case Notes Analysis (ML)
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setSelectedMemberId(member.member_id);
                                    setMemberDialogOpen(true);
                                  }}
                                >
                                  <ClipboardList className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('retention.view_details', 'View Details')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!member.phone_number}
                                  onClick={() => window.location.href = `tel:${member.phone_number}`}
                                >
                                  <Phone className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('retention.call_member', 'Call Member')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {allMembers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <div className="space-y-3">
                          <p className="text-muted-foreground">
                            {selectedRiskFilter
                              ? `No members found with ${selectedRiskFilter} risk level`
                              : 'No members found with engagement scores'}
                          </p>
                          {!selectedRiskFilter && dashboardData && dashboardData.total_members > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm text-muted-foreground">
                                {dashboardData.total_members} member(s) exist but engagement scores haven't been calculated yet.
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleRefreshScores}
                                disabled={refreshing}
                              >
                                {refreshing ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                {refreshing ? 'Calculating Scores...' : 'Calculate Engagement Scores'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {membersTotalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={membersPage}
                  totalPages={membersTotalPages}
                  totalItems={membersTotal}
                  pageSize={25}
                  onPageChange={(page) => setMembersPage(page)}
                  showPageSize={false}
                  showItemCount={true}
                />
              </div>
            )}
          </TabsContent>

          {/* At-Risk Members Tab */}
          <TabsContent value="at-risk" className="py-6">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('retention.member', 'Member')}</TableHead>
                    <TableHead className="text-center">{t('retention.score', 'Score')}</TableHead>
                    <TableHead className="text-center">{t('retention.risk', 'Risk')}</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Brain className="h-4 w-4 text-primary" />
                        {t('retention.churn_risk', 'Churn Risk')}
                      </div>
                    </TableHead>
                    <TableHead className="text-center">{t('retention.trend', 'Trend')}</TableHead>
                    <TableHead className="text-center">{t('retention.last_contact', 'Last Contact')}</TableHead>
                    <TableHead className="text-center">{t('retention.best_channel', 'Best Channel')}</TableHead>
                    <TableHead className="text-center">{t('retention.alerts_1', 'Alerts')}</TableHead>
                    <TableHead className="text-center">ML Insights</TableHead>
                    <TableHead className="text-center">{t('retention.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {atRiskMembers.map((member) => (
                    <TableRow key={member.engagement_score_id}>
                      <TableCell>
                        <div>
                          <div className="text-sm font-medium">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {member.program_name || 'No program'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm font-bold">
                            {member.engagement_score.toFixed(0)}
                          </span>
                          <Progress
                            value={member.engagement_score}
                            className="w-12 h-2"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getRiskBadgeVariant(member.risk_level)}>
                          {member.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <ChurnPredictionBadge
                          prediction={{
                            churn_probability_14d: member.churn_probability_14d,
                            ml_risk_level: member.ml_risk_level,
                          }}
                          horizon={14}
                          size="small"
                          showDetails={true}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              {getTrendIcon(member.trend_direction)}
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.trend_direction}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          {member.days_since_last_contact !== null
                            ? `${member.days_since_last_contact}d ago`
                            : 'Never'}
                        </div>
                        {member.last_contact_outcome && (
                          <div className="text-xs text-muted-foreground">
                            {member.last_contact_outcome}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              {getChannelIcon(member.preferred_channel)}
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.preferred_channel
                                ? `Best time: ${getDayName(member.best_day_of_week)} ${member.best_hour_start || 9}:00-${member.best_hour_end || 17}:00`
                                : 'No preference data'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-center">
                        {member.open_alert_count > 0 && (
                          <div className="relative inline-block">
                            <Bell className="h-4 w-4 text-red-600" />
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-600 rounded-full text-[8px] text-white flex items-center justify-center">
                              {member.open_alert_count}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      {/* ML Insights Column for At-Risk Members */}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                  onClick={() => {
                                    setSelectedMemberId(member.member_id);
                                    setTabValue('communication-features');
                                  }}
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Communication Features (ML)
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  onClick={() => {
                                    setSelectedMemberId(member.member_id);
                                    setTabValue('case-notes-analysis');
                                  }}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Case Notes Analysis (ML)
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setSelectedMemberId(member.member_id);
                                    setMemberDialogOpen(true);
                                  }}
                                >
                                  <ClipboardList className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('retention.view_details', 'View Details')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!member.phone_number}
                                  onClick={() => window.location.href = `tel:${member.phone_number}`}
                                >
                                  <Phone className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('retention.call_member', 'Call Member')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {atRiskMembers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground">
                        {t('retention.no_atrisk_members_found_great', 'No at-risk members found. Great job!')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="py-6">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('retention.alert', 'Alert')}</TableHead>
                    <TableHead>{t('retention.member_1', 'Member')}</TableHead>
                    <TableHead className="text-center">{t('retention.severity', 'Severity')}</TableHead>
                    <TableHead className="text-center">{t('retention.status', 'Status')}</TableHead>
                    <TableHead className="text-center">SLA</TableHead>
                    <TableHead className="text-center">{t('retention.created', 'Created')}</TableHead>
                    <TableHead className="text-center">{t('retention.actions_1', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {alert.alert_title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {alert.alert_type}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{alert.member_name || '-'}</div>
                        {alert.engagement_score && (
                          <Badge variant="outline">
                            Score: {alert.engagement_score.toFixed(0)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            alert.alert_severity === 'critical'
                              ? 'destructive'
                              : alert.alert_severity === 'high'
                                ? 'default'
                                : 'secondary'
                          }
                        >
                          {alert.alert_severity === 'critical' && (
                            <AlertOctagon className="h-3 w-3 mr-1" />
                          )}
                          {alert.alert_severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{alert.status}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {alert.sla_due_at && (
                          <span className={`text-sm ${alert.sla_breached ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                            {alert.sla_breached ? 'BREACHED' : fmt.short(alert.sla_due_at)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {fmt.short(alert.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {alert.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                          >
                            {t('retention.acknowledge', 'Acknowledge')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {alerts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        {t('retention.no_open_alerts', 'No open alerts')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="geographic-risk-map" className="py-6">
            <GeographicRiskMap cboId={cboId} />
          </TabsContent>

          <TabsContent value="live-prediction" className="py-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Label htmlFor="member-select-live">Select Member:</Label>
                <Select
                  value={selectedMemberId?.toString() || ''}
                  onValueChange={(value) => setSelectedMemberId(value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="member-select-live" className="w-[300px]">
                    <SelectValue placeholder="Select a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allMembers.length > 0 ? (
                      allMembers.map((member) => (
                        <SelectItem key={member.member_id} value={member.member_id.toString()}>
                          {member.first_name} {member.last_name} - {member.risk_level}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>Loading members...</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedMemberId ? (
                <LivePredictionPanel memberId={selectedMemberId} />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a member from the dropdown above to view their live churn prediction.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>

          <TabsContent value="model-performance" className="py-6">
            <ModelPerformanceDashboard />
          </TabsContent>

          <TabsContent value="communication-features" className="py-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Label htmlFor="member-select-comm">Select Member:</Label>
                <Select
                  value={selectedMemberId?.toString() || ''}
                  onValueChange={(value) => setSelectedMemberId(value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="member-select-comm" className="w-[300px]">
                    <SelectValue placeholder="Select a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allMembers.length > 0 ? (
                      allMembers.map((member) => (
                        <SelectItem key={member.member_id} value={member.member_id.toString()}>
                          {member.first_name} {member.last_name} - {member.risk_level}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>Loading members...</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedMemberId ? (
                <CommunicationFeaturesPanel memberId={selectedMemberId} />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a member from the dropdown above to view their communication features.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>

          <TabsContent value="case-notes-analysis" className="py-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Label htmlFor="member-select-notes">Select Member:</Label>
                <Select
                  value={selectedMemberId?.toString() || ''}
                  onValueChange={(value) => setSelectedMemberId(value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="member-select-notes" className="w-[300px]">
                    <SelectValue placeholder="Select a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allMembers.length > 0 ? (
                      allMembers.map((member) => (
                        <SelectItem key={member.member_id} value={member.member_id.toString()}>
                          {member.first_name} {member.last_name} - {member.risk_level}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>Loading members...</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedMemberId ? (
                <CaseNotesAnalysis memberId={selectedMemberId} cboId={cboId} />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a member from the dropdown above to view their case notes analysis.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>

          <TabsContent value="cbo-data-entry" className="py-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Label htmlFor="member-select-cbo">Select Member:</Label>
                <Select
                  value={selectedMemberId?.toString() || ''}
                  onValueChange={(value) => setSelectedMemberId(value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="member-select-cbo" className="w-[300px]">
                    <SelectValue placeholder="Select a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allMembers.length > 0 ? (
                      allMembers.map((member) => (
                        <SelectItem key={member.member_id} value={member.member_id.toString()}>
                          {member.first_name} {member.last_name} - {member.risk_level}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>Loading members...</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedMemberId ? (
                <CBODataEntry memberId={selectedMemberId} cboId={cboId} />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p><strong>CBO Data Entry</strong> allows you to enter custom SDOH (Social Determinants of Health) data for members.</p>
                      <p>Please select a member from the dropdown above to enter or view their custom CBO data, including:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1">
                        <li>Population focus and cultural community</li>
                        <li>Language preferences and barriers</li>
                        <li>Social risk factors</li>
                        <li>Behavioral health risks</li>
                        <li>Safety concerns</li>
                        <li>Chronic conditions</li>
                        <li>Medication adherence</li>
                        <li>Case notes summary</li>
                      </ul>
                      <p className="mt-2">This data is automatically integrated into ML predictions to improve retention risk assessment.</p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </Card>

      {/* Member Detail Dialog - Wider layout with side-by-side panels */}
      <Dialog
        open={memberDialogOpen}
        onOpenChange={setMemberDialogOpen}
      >
        {selectedMember && (
          <DialogContent className="max-w-[95vw] w-full lg:max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {selectedMember.first_name} {selectedMember.last_name}
                  <Badge variant={getRiskBadgeVariant(selectedMember.risk_level)}>
                    {selectedMember.risk_level}
                  </Badge>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {/* LEFT COLUMN: AI Prediction & Member Details */}
              <div className="lg:col-span-5 space-y-4">
                {/* ML Prediction Insights Panel */}
                <MLPredictionInsightsPanel
                  memberId={selectedMember.member_id}
                  enrollmentId={selectedMember.enrollment_id}
                  compact={false}
                />

                {/* Score Breakdown - Compact */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-3">
                      {t('retention.engagement_score_breakdown', 'Engagement Score Breakdown')}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {t('retention.communication', 'Communication')}
                        </p>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={selectedMember.communication_score}
                            className="flex-1 h-2"
                          />
                          <span className="text-xs font-bold">
                            {selectedMember.communication_score.toFixed(0)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {t('retention.activity', 'Activity')}
                        </p>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={selectedMember.activity_score}
                            className="flex-1 h-2"
                          />
                          <span className="text-xs font-bold">
                            {selectedMember.activity_score.toFixed(0)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {t('retention.recency', 'Recency')}
                        </p>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={selectedMember.recency_score}
                            className="flex-1 h-2"
                          />
                          <span className="text-xs font-bold">
                            {selectedMember.recency_score.toFixed(0)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {t('retention.data_quality', 'Data Quality')}
                        </p>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={selectedMember.data_quality_score}
                            className="flex-1 h-2"
                          />
                          <span className="text-xs font-bold">
                            {selectedMember.data_quality_score.toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Info & Activity - Compact */}
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          {t('retention.contact_information', 'Contact Info')}
                        </h4>
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <Phone className="h-4 w-4" />
                          {selectedMember.phone_number || 'No phone'}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4" />
                          {selectedMember.email || 'No email'}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          {t('retention.optimal_contact_time', 'Best Contact Time')}
                        </h4>
                        <div className="text-sm">
                          {selectedMember.preferred_channel || 'Phone'} · {getDayName(selectedMember.best_day_of_week)}
                        </div>
                        <div className="text-sm">
                          {selectedMember.best_hour_start || 9}:00 - {selectedMember.best_hour_end || 17}:00
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        Last contact: {selectedMember.days_since_last_contact ?? 'Never'} days ago ·
                        Attempts (7d): {selectedMember.contact_attempts_7d} ·
                        Outcome: {selectedMember.last_contact_outcome || 'N/A'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* RIGHT COLUMN: Action Plan */}
              <div className="lg:col-span-7">
                <MemberActionPlanEnhanced
                  member={{
                    member_id: selectedMember.member_id,
                    first_name: selectedMember.first_name,
                    last_name: selectedMember.last_name,
                    phone_number: selectedMember.phone_number,
                    email: selectedMember.email,
                    engagement_score: selectedMember.engagement_score,
                    risk_level: selectedMember.risk_level,
                    trend_direction: selectedMember.trend_direction,
                    days_since_last_contact: selectedMember.days_since_last_contact,
                    days_since_last_activity: selectedMember.days_since_last_activity,
                    communication_score: selectedMember.communication_score,
                    activity_score: selectedMember.activity_score,
                    recency_score: selectedMember.recency_score,
                    data_quality_score: selectedMember.data_quality_score,
                    preferred_channel: selectedMember.preferred_channel,
                    best_day_of_week: selectedMember.best_day_of_week,
                    best_hour_start: selectedMember.best_hour_start,
                    best_hour_end: selectedMember.best_hour_end,
                    churn_probability: selectedMember.churn_probability_14d,
                    contributing_factors: selectedMember.contributing_factors,
                    last_contact_outcome: selectedMember.last_contact_outcome,
                    contact_attempts_7d: selectedMember.contact_attempts_7d,
                    program_name: selectedMember.program_name,
                    enrollment_id: selectedMember.enrollment_id,
                  }}
                  onOpenPhoneDialog={(member) => {
                    // Close member dialog first to avoid nested dialog aria-hidden issues
                    setMemberDialogOpen(false);
                    setTimeout(() => {
                      setCommunicationMember(member);
                      setPhoneDialogOpen(true);
                    }, 100);
                  }}
                  onOpenSmsDialog={(member) => {
                    setMemberDialogOpen(false);
                    setTimeout(() => {
                      setCommunicationMember(member);
                      setSmsDialogOpen(true);
                    }, 100);
                  }}
                  onOpenEmailDialog={(member) => {
                    setMemberDialogOpen(false);
                    setTimeout(() => {
                      setCommunicationMember(member);
                      setEmailDialogOpen(true);
                    }, 100);
                  }}
                  onCreateTask={async (taskData) => {
                    try {
                      const response = await authFetch('/api/tasks', {
                        method: 'POST',
                        jsonBody: {
                          ...taskData,
                          cbo_id: cboId,
                          status: 'pending',
                          task_type: 'retention_action',
                        }
                      });
                      if (response.ok) {
                        // Task created successfully
                        console.log('Task created from retention action');
                      }
                    } catch (error) {
                      console.error('Failed to create task:', error);
                    }
                  }}
                  onCommunicationSent={() => {
                    // Refresh data after communication
                    fetchDashboardData();
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>
                {t('retention.close', 'Close')}
              </Button>
              <Button
                onClick={() => {
                  // Close member dialog first to avoid nested dialog aria-hidden issues
                  setMemberDialogOpen(false);
                  setTimeout(() => {
                    setCommunicationMember({
                      member_id: selectedMember.member_id,
                      first_name: selectedMember.first_name,
                      last_name: selectedMember.last_name,
                      phone_number: selectedMember.phone_number,
                      email: selectedMember.email,
                      enrollment_id: selectedMember.enrollment_id,
                    });
                    setPhoneDialogOpen(true);
                  }, 100);
                }}
                disabled={!selectedMember.phone_number}
              >
                <Phone className="h-4 w-4 mr-2" />
                {t('retention.call_now', 'Call Now')}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Phone Communication Dialog - rendered outside member dialog to avoid aria-hidden issues */}
      <Dialog
        open={phoneDialogOpen}
        onOpenChange={setPhoneDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                {t('retention.call_member', 'Call Member')} - {communicationMember?.first_name} {communicationMember?.last_name}
              </div>
            </DialogTitle>
          </DialogHeader>
          {communicationMember && (
            <div className="py-4">
              <Alert className="mb-4">
                <AlertDescription>
                  {t('retention.initiating_call', 'Initiating call to')} {communicationMember.phone_number}...
                  {t('retention.call_logged', 'This call will be logged to the member\'s conversation history.')}
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                {t('retention.phone_interface_placeholder', 'Phone interface will load here. All calls are recorded and logged to member conversations.')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPhoneDialogOpen(false)}>
              {t('retention.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Communication Dialog */}
      <Dialog
        open={smsDialogOpen}
        onOpenChange={setSmsDialogOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                {t('retention.send_sms', 'Send SMS')} - {communicationMember?.first_name} {communicationMember?.last_name}
              </div>
            </DialogTitle>
          </DialogHeader>
          {communicationMember && (
            <div className="py-4 space-y-4">
              <Alert>
                <AlertDescription>
                  {t('retention.sms_logged', 'This SMS will be logged to the member\'s conversation history in the enrollment.')}
                </AlertDescription>
              </Alert>
              <div>
                <Label htmlFor="sms-phone">{t('retention.phone_number', 'Phone Number')}</Label>
                <Input
                  id="sms-phone"
                  value={communicationMember.phone_number || ''}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="sms-message">{t('retention.message', 'Message')}</Label>
                <textarea
                  id="sms-message"
                  rows={4}
                  placeholder={t('retention.type_sms_message', 'Type your SMS message here...')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('retention.sms_character_limit', 'SMS messages are limited to 160 characters for single messages.')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
              {t('retention.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={async () => {
                // Send SMS via internal API
                try {
                  const response = await authFetch(getWeaveinApiUrl(`/api/members/${communicationMember?.member_id}/communications/sms`), {
                    method: 'POST',
                    jsonBody: {
                      to_number: communicationMember?.phone_number,
                      enrollment_id: communicationMember?.enrollment_id,
                      member_id: communicationMember?.member_id,
                      message_type: 'retention_outreach',
                    }
                  });
                  if (response.ok) {
                    setSmsDialogOpen(false);
                    fetchDashboardData();
                  }
                } catch (error) {
                  console.error('Failed to send SMS:', error);
                }
              }}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('retention.send_sms', 'Send SMS')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Communication Dialog */}
      <Dialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                {t('retention.send_email', 'Send Email')} - {communicationMember?.first_name} {communicationMember?.last_name}
              </div>
            </DialogTitle>
          </DialogHeader>
          {communicationMember && (
            <div className="py-4 space-y-4">
              <Alert>
                <AlertDescription>
                  {t('retention.email_logged', 'This email will be logged to the member\'s conversation history in the enrollment.')}
                </AlertDescription>
              </Alert>
              <div>
                <Label htmlFor="email-to">{t('retention.to_email', 'To')}</Label>
                <Input
                  id="email-to"
                  value={communicationMember.email || ''}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="email-subject">{t('retention.subject', 'Subject')}</Label>
                <Input
                  id="email-subject"
                  placeholder={t('retention.email_subject_placeholder', 'Enter email subject...')}
                />
              </div>
              <div>
                <Label htmlFor="email-message">{t('retention.message', 'Message')}</Label>
                <textarea
                  id="email-message"
                  rows={6}
                  placeholder={t('retention.type_email_message', 'Type your email message here...')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              {t('retention.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={async () => {
                // Send email via internal API
                try {
                  const response = await authFetch(getWeaveinApiUrl(`/api/members/${communicationMember?.member_id}/communications/email`), {
                    method: 'POST',
                    jsonBody: {
                      to_email: communicationMember?.email,
                      enrollment_id: communicationMember?.enrollment_id,
                      member_id: communicationMember?.member_id,
                      email_type: 'retention_outreach',
                    }
                  });
                  if (response.ok) {
                    setEmailDialogOpen(false);
                    fetchDashboardData();
                  }
                } catch (error) {
                  console.error('Failed to send email:', error);
                }
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              {t('retention.send_email', 'Send Email')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberRetentionDashboard;
